import { useRef, useState, useCallback } from "react";
import { Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  validateComprobanteFile,
  validateAttachmentFile,
  COMPROBANTE_ACCEPT,
  ATTACHMENT_ACCEPT,
  DEFAULT_MAX_SIZE_MB,
} from "@/shared/utils/fileValidation";

type MultiFileUploadProps = {
  value: File[];
  onChange: (files: File[]) => void;
  accept?: string;
  maxSizeMB?: number;
  label?: string;
  description?: string;
  instructionText?: string;
  error?: string;
  disabled?: boolean;
  inputId?: string;
  allowPdf?: boolean;
};

export function MultiFileUpload({
  value,
  onChange,
  accept,
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
  label = "Comprobante (imágenes)",
  description = "PNG, JPG, WebP hasta 10 MB. Puedes elegir varios archivos.",
  instructionText,
  error: externalError,
  disabled = false,
  inputId = "multi-file-upload-input",
  allowPdf = false,
}: MultiFileUploadProps) {
  const resolvedAccept = accept ?? (allowPdf ? ATTACHMENT_ACCEPT : COMPROBANTE_ACCEPT);
  const validateFile = allowPdf ? validateAttachmentFile : validateComprobanteFile;

  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayError = validationError ?? externalError ?? null;

  const validateAndAdd = useCallback(
    (newFiles: File[]) => {
      if (newFiles.length === 0) return;
      const errors: string[] = [];
      const validFiles: File[] = [];
      for (const file of newFiles) {
        const result = validateFile(file, maxSizeMB);
        if (!result.valid) {
          errors.push(`${file.name}: ${result.error}`);
        } else {
          validFiles.push(file);
        }
      }
      if (errors.length > 0) {
        setValidationError(errors[0]);
        if (inputRef.current) inputRef.current.value = "";
        return;
      }
      setValidationError(null);
      onChange([...value, ...validFiles]);
      if (inputRef.current) inputRef.current.value = "";
    },
    [maxSizeMB, onChange, value, validateFile]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      const files = e.target.files ? Array.from(e.target.files) : [];
      validateAndAdd(files);
    },
    [disabled, validateAndAdd]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled || !e.dataTransfer.files?.length) return;
      const files = Array.from(e.dataTransfer.files);
      validateAndAdd(files);
    },
    [disabled, validateAndAdd]
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const removeFile = useCallback(
    (index: number) => {
      if (disabled) return;
      setValidationError(null);
      const next = value.filter((_, i) => i !== index);
      onChange(next);
    },
    [disabled, onChange, value]
  );

  const clearAll = useCallback(() => {
    if (disabled) return;
    setValidationError(null);
    onChange([]);
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.files = null;
    }
  }, [disabled, onChange]);

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
          {label}
        </label>
      )}

      <input
        ref={inputRef}
        type="file"
        id={inputId}
        accept={resolvedAccept}
        disabled={disabled}
        multiple
        onChange={handleChange}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className="sr-only"
      />

      <div
        role="button"
        tabIndex={0}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        className={cn(
          "border-2 border-dashed rounded-xl px-6 py-6 flex flex-col items-center justify-center space-y-2 transition-all cursor-pointer group",
          disabled && "opacity-50 cursor-not-allowed",
          !disabled && "hover:bg-gray-100 dark:hover:bg-gray-800",
          displayError && "border-red-300 dark:border-red-600 bg-red-50/30 dark:bg-red-900/20",
          !displayError && "border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50"
        )}
      >
        <Upload className="h-7 w-7 text-green-500 dark:text-green-400" />
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {instructionText ?? "Clic para seleccionar o arrastra archivos aquí"}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-500">{description}</p>
      </div>

      {value.length > 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
              {value.length} archivo{value.length !== 1 ? "s" : ""} seleccionado{value.length !== 1 ? "s" : ""}
            </span>
            <button
              type="button"
              onClick={clearAll}
              disabled={disabled}
              className="text-xs font-medium text-rose-500 hover:text-rose-600 dark:hover:text-rose-400 disabled:opacity-50"
            >
              Quitar todos
            </button>
          </div>
          <ul className="space-y-1.5 max-h-32 overflow-y-auto">
            {value.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center justify-between gap-2 text-sm text-gray-700 dark:text-gray-300"
              >
                <span className="truncate min-w-0">{file.name}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(index);
                  }}
                  disabled={disabled}
                  className="shrink-0 p-1 text-rose-500 hover:text-rose-600 dark:hover:text-rose-400 rounded disabled:opacity-50"
                  aria-label="Quitar archivo"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {displayError && (
        <p className="text-sm text-red-500 dark:text-red-400" role="alert">
          {displayError}
        </p>
      )}
    </div>
  );
}
